import React, { useState } from "react";
import { View, StyleSheet, ActivityIndicator, useWindowDimensions } from "react-native";
import { useRoute, RouteProp, useNavigation } from "@react-navigation/native";
import { MotiView } from "moti";
import { RootStackParamList } from "../../navigation/navigation.types";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import StudentInsightsProfileHeader from "../../components/adminDashboard/Analytics/components/StudentInsightsProfileHeader";
import StudentProfileTabs from "../../components/adminDashboard/Analytics/components/StudentProfileTabs";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { StudentProfileTab } from "../../constants/studentProfileTabs";
import RosterInsightByModuleContainer from "../../containers/adminDashboard/AdminCourseAnalyticsContainer/CourseRosterInsightParticularStudentContainer/RosterInsightByModuleContainer";
import RosterInsightOverviewContainer from "../../containers/adminDashboard/AdminCourseAnalyticsContainer/CourseRosterInsightParticularStudentContainer/RosterInsightOverviewContainer";
import { useGetCourseStudentInsightsQuery } from "../../graphql/generated/graphql";
import { formatDate, formatTimeAgo } from "../../utils/date";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { colors, spacing } from "../../styles/design-system";

const TabData = [
  { key: StudentProfileTab.OVERVIEW, title: "Overview" },
  { key: StudentProfileTab.BY_MODULE, title: "By Module" },
  // { key: StudentProfileTab.GRADES_AND_SUBMISSIONS, title: "Grades & Submissions" },
  // { key: StudentProfileTab.INTERVENTIONS, title: "Interventions" },
];

function CourseInformationParticularStudentView() {
  const route = useRoute<RouteProp<RootStackParamList, "Course Information For Particular Student">>();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const { studentId, courseName, courseId } = route.params || {};
  const [activeTab, setActiveTab] = useState<string>(StudentProfileTab.OVERVIEW);
  const [isHovered, setIsHovered] = useState(false);
  const containerPadding = width >= 1280 ? 32 : width >= 768 ? 24 : 16;
  const handleBackToCourse = () => {
    const returnTo = route.params?.returnTo || "Course Information";
    navigation.navigate(
      returnTo as never,
      {
        courseId,
        courseName,
        returnTo: "Manage Courses",
      } as never
    );
  };

  const {
    data: studentData,
    loading: studentDataLoading,
    error: studentDataError,
  } = useGetCourseStudentInsightsQuery({
    variables: { courseId, studentId },
    skip: !courseId || !studentId,
  });
  const studentProfileInsights = studentData?.getCourseStudentInsights?.data;

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case StudentProfileTab.OVERVIEW:
        return <RosterInsightOverviewContainer studentInsights={studentProfileInsights ?? null} />;
      case StudentProfileTab.BY_MODULE:
        return <RosterInsightByModuleContainer studentInsights={studentProfileInsights ?? null} />;
      case StudentProfileTab.GRADES_AND_SUBMISSIONS:
        return <></>;
      case StudentProfileTab.INTERVENTIONS:
        return <></>;
    }
  };

  if (studentDataLoading) {
    return (
      <AdminLayout withPadding={true}>
        <View style={[styles.container, { paddingHorizontal: containerPadding }]}>
          <CustomButton
            text="Back to course"
            styleType="transparent"
            onPress={handleBackToCourse}
            useTranslationText={false}
            style={[styles.backButton, isHovered && styles.backButtonHovered]}
            textStyle={styles.backButtonText}
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
            leftIcon={<ArrowLeftIcon size={12} color="#64748B" />}
          />
          <View style={styles.loadingStateContainer}>
            <ActivityIndicator size="large" color={colors.blue[500]} />
            <CustomText
              text="Loading student insights..."
              size="m"
              useTranslationText={false}
              style={styles.stateText}
            />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (studentDataError || !studentProfileInsights) {
    return (
      <AdminLayout withPadding={true}>
        <View style={[styles.container, { paddingHorizontal: containerPadding }]}>
          <CustomButton
            text="Back to course"
            styleType="transparent"
            onPress={handleBackToCourse}
            useTranslationText={false}
            style={[styles.backButton, isHovered && styles.backButtonHovered]}
            textStyle={styles.backButtonText}
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
            leftIcon={<ArrowLeftIcon size={12} color="#64748B" />}
          />
          <View style={styles.loadingStateContainer}>
            <CustomText
              text="Unable to load student insights."
              size="m"
              weight="bold"
              useTranslationText={false}
              style={styles.errorText}
            />
            <CustomText
              text="Please refresh the page or try again in a moment."
              size="s"
              useTranslationText={false}
              style={styles.stateText}
            />
          </View>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout withPadding={true}>
      <View style={[styles.container, { paddingHorizontal: containerPadding }]}>
        <CustomButton
          text="Back to course"
          styleType="transparent"
          onPress={handleBackToCourse}
          useTranslationText={false}
          style={[styles.backButton, isHovered && styles.backButtonHovered]}
          textStyle={styles.backButtonText}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          leftIcon={<ArrowLeftIcon size={12} color="#64748B" />}
        />
        <View style={styles.contentContainer}>
          <StudentInsightsProfileHeader
            studentName={studentProfileInsights?.student.studentName || ""}
            email={studentProfileInsights?.email || ""}
            lastActive={
              studentProfileInsights?.student.lastActiveAt
                ? formatTimeAgo(studentProfileInsights.student.lastActiveAt)
                : studentProfileInsights?.student.statusSummary || ""
            }
            enrolledDate={
              studentProfileInsights?.enrolledDate ? formatDate(studentProfileInsights.enrolledDate, false) : ""
            }
            riskStatus={studentProfileInsights?.student.riskLevel || ""}
            sentiment={studentProfileInsights?.student.sentiment || ""}
            probation={studentProfileInsights?.student.riskLevel === "AT_RISK"}
            overallProgress={studentProfileInsights?.student.progressPct || 0}
            onSendMessage={() => null}
          />

          <CustomSpacing type="vertical" size="xl" />

          <View style={styles.tabContainer}>
            <StudentProfileTabs
              tabData={TabData}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              loading={studentDataLoading}
            />
            <MotiView
              key={activeTab}
              from={{ opacity: 0, translateY: 40 }}
              animate={{ opacity: 1, translateY: 0 }}
              delay={10}
              // @ts-expect-error - transition is not typed
              transition={{ type: "timing", duration: 300 }}>
              {renderActiveTabContent()}
            </MotiView>
          </View>
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    maxWidth: 1280,
    width: "100%",
    alignSelf: "center",
    paddingVertical: spacing.lg,
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  backButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 0,
  },
  backButtonText: {
    color: "#64748B",
    fontSize: 12,
  },
  backButtonHovered: {
    transform: [{ scale: 1.05 }],
  },
  tabContainer: {
    marginTop: 24,
    flex: 1,
  },
  loadingStateContainer: {
    flex: 1,
    minHeight: 480,
    alignItems: "center",
    justifyContent: "center",
    gap: spacing.md,
  },
  stateText: {
    color: colors.grey[600],
    textAlign: "center",
  },
  errorText: {
    color: colors.grey[900],
    textAlign: "center",
  },
});

export default CourseInformationParticularStudentView;
